import { useMemo } from 'react'
import {
  getWeeklyActivityForLearner,
  type DayActivity,
} from '../../../utils/activityHelpers'
import { AppState } from '../../../app/types'

/**
 * Shared hook: derive the 7-day rhythm-array for the current week from a
 * child's question-history. Memoized on `questionStates` reference so that
 * pages re-rendering for other reasons (selector pills, navigation) don't
 * recompute.
 *
 * W4-6 (audit C-P-6): eliminates duplicate inline useMemo blocks across
 * HomePage / TipsPage / ActivityPage. ActivityPage keeps its larger composite
 * useMemo (it computes 4-week calendar + streak-stats + accuracy + recent
 * sessions in one pass) but consumes this hook's `weeklyActivity` slice via
 * a re-export from the same source utility.
 *
 * Returns:
 * - `weeklyActivity`: 7 DayActivity objects, Mon → Sun
 * - `daysPracticed`: count of days where the child practiced this week (0–7)
 */
export function useWeeklyActivity(childData: AppState | null | undefined): {
  weeklyActivity: DayActivity[]
  daysPracticed: number
} {
  return useMemo(() => {
    const weekly = getWeeklyActivityForLearner(childData?.questionStates || {})
    const daysPracticed = weekly.filter((d) => d.practiced).length
    return { weeklyActivity: weekly, daysPracticed }
  }, [childData?.questionStates])
}
